// @pta/validation — Zod sémák, BE és FE közös használatra
import { z } from 'zod';

// -------- Educational ID — ország-specifikus checksum validáció --------
//
// FONTOS: a `validateEducationalId` és társai itt vannak inline-ban, NEM külön
// fájlban. A `@pta/validation` package-nek nincs build step-je (workspace+vite
// közvetlenül a `src/`-t resolveli), és a BE Node ESM runtime `.js` extension-t
// követel a relatív import-okhoz — `from './educational-id.js'` ezért megbukna,
// mert csak `.ts` lenne ott. Inline-ban viszont csak az `index.ts` van importálva,
// és az működik.

export type EducationalIdValidationOk = { ok: true };
export type EducationalIdValidationFail = {
  ok: false;
  errorKey:
    | 'educational_id.invalid_format'
    | 'educational_id.invalid_checksum'
    | 'educational_id.unsupported_country';
};
export type EducationalIdValidationResult =
  | EducationalIdValidationOk
  | EducationalIdValidationFail;

export interface EducationalIdValidator {
  description: string;
  normalize?: (value: string) => string;
  validate: (normalized: string) => EducationalIdValidationResult;
}

/**
 * Magyar Oktatási azonosító (11 számjegy):
 *  - 1. jegy fix '7'.
 *  - 1-10. jegyek súlyozása sorszám szerint (i = 1..10).
 *  - Σ(digit_i * i) mod 11 == 11. jegy (ellenőrzőszám).
 */
function validateHuEducationalId(value: string): EducationalIdValidationResult {
  if (!/^7[0-9]{10}$/.test(value)) {
    return { ok: false, errorKey: 'educational_id.invalid_format' };
  }
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += (value.charCodeAt(i) - 48) * (i + 1);
  }
  const expectedCheck = sum % 11;
  const actualCheck = value.charCodeAt(10) - 48;
  if (expectedCheck !== actualCheck) {
    return { ok: false, errorKey: 'educational_id.invalid_checksum' };
  }
  return { ok: true };
}

const huValidator: EducationalIdValidator = {
  description: 'HU: 11 számjegy, 7-essel kezdő, sorszám-súlyozott modulo-11 ellenőrzőszámmal.',
  normalize: (v) => v.replace(/[\s-]/g, ''),
  validate: validateHuEducationalId,
};

/**
 * Új ország felvétele: új sor itt.
 */
export const EDUCATIONAL_ID_VALIDATORS: Record<string, EducationalIdValidator> = {
  HU: huValidator,
};

export function validateEducationalId(
  value: string,
  countryCode: string,
): EducationalIdValidationResult {
  const cc = countryCode.toUpperCase();
  const validator = EDUCATIONAL_ID_VALIDATORS[cc];
  if (!validator) {
    return { ok: false, errorKey: 'educational_id.unsupported_country' };
  }
  const normalized = validator.normalize ? validator.normalize(value) : value;
  return validator.validate(normalized);
}

export function isValidEducationalId(value: string, countryCode: string): boolean {
  return validateEducationalId(value, countryCode).ok;
}

// -------- Country / lokalizáció --------

export const CountryCodeSchema = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/, 'ISO 3166-1 alpha-2 kód, nagybetű');

export const CurrencyCodeSchema = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/, 'ISO 4217 currency kód, 3 nagybetű');

export const NumberFormatSchema = z.object({
  decimal: z.string().min(1),
  thousand: z.string(),
  currencyPosition: z.enum(['before', 'after']),
});

export const CountrySchema = z.object({
  code: CountryCodeSchema,
  nameKey: z.string().min(1),
  currency: z.string().length(3),
  locale: z.string().min(2),
  dateFormat: z.string().min(1),
  numberFormat: NumberFormatSchema,
  allowedSecondaryCurrencies: z.array(CurrencyCodeSchema),
  isActive: z.boolean(),
});

// -------- School year --------

export const SchoolYearSchema = z.object({
  id: z.number().int().positive(),
  countryCode: CountryCodeSchema,
  displayName: z.string().min(1),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  isCurrent: z.boolean(),
});

// -------- Institution types --------

export const InstitutionPrimaryTypeSchema = z.object({
  id: z.number().int().positive(),
  countryCode: CountryCodeSchema,
  code: z.string().min(1),
  nameKey: z.string().min(1),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});

export const InstitutionSecondaryTypeSchema = z.object({
  id: z.number().int().positive(),
  primaryTypeId: z.number().int().positive(),
  code: z.string().min(1),
  nameKey: z.string().min(1),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});

// -------- Group types --------

export const GroupTypeSchema = z.object({
  id: z.number().int().positive(),
  countryCode: CountryCodeSchema,
  code: z.string().min(1),
  nameKey: z.string().min(1),
  suggestedForPrimaryTypeId: z.number().int().positive().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});

// -------- Users + roles --------

export const ScopeTypeSchema = z.enum(['system', 'country', 'institution', 'group']);

export const RoleSchema = z.enum([
  'systemadmin',
  'applicationadmin',
  'country_admin',
  'institution_admin',
  'pta_leader',
  'pta_cashier',
  'parent',
  'student',
  'b2b_supplier',
]);

export const UserPublicSchema = z.object({
  id: z.number().int().positive(),
  email: z.string().email(),
  displayName: z.string().min(1),
  isActive: z.boolean(),
});

export const ScopeRefSchema = z
  .object({
    type: ScopeTypeSchema,
    id: z.string().nullable(),
  })
  .refine(
    (s) => (s.type === 'system' ? s.id === null : s.id !== null && s.id.length > 0),
    { message: 'scope.id must be null if scope.type is system, otherwise non-empty' },
  );

export const InstitutionBindingStatusSchema = z.enum(['free_text', 'claimed', 'verified']);

export const IdentityStatusSchema = z.enum(['informal', 'identified', 'verified']);

// -------- Institutions --------

export const InstitutionCreateSchema = z.object({
  countryCode: CountryCodeSchema,
  primaryTypeId: z.number().int().positive(),
  officialId: z.string().min(1).max(50),
  name: z.string().min(1).max(500),
  shortName: z.string().max(200).nullable().optional(),
  addressLine: z.string().max(500).nullable().optional(),
  city: z.string().max(200).nullable().optional(),
  postalCode: z.string().max(20).nullable().optional(),
  phone: z.string().max(50).nullable().optional(),
  email: z.string().email().max(200).nullable().optional(),
  website: z.string().url().max(500).nullable().optional(),
  notes: z.string().nullable().optional(),
  secondaryTypeIds: z.array(z.number().int().positive()).optional(),
});

export const InstitutionUpdateSchema = InstitutionCreateSchema.partial().omit({
  countryCode: true,
});

// -------- Groups --------

/**
 * Csoport-létrehozás. Két út (free_text vagy institution-bound), a refine biztosítja a konzisztenciát.
 */
export const GroupCreateSchema = z
  .object({
    institutionId: z.number().int().positive().nullable().optional(),
    institutionFreeText: z.string().min(1).max(500).nullable().optional(),
    institutionBindingStatus: InstitutionBindingStatusSchema.optional(),
    schoolYearId: z.number().int().positive(),
    groupTypeId: z.number().int().positive(),
    name: z.string().min(1).max(100),
    notes: z.string().nullable().optional(),
  })
  .refine(
    (v) => {
      const hasInst = v.institutionId !== undefined && v.institutionId !== null;
      const hasFreeText =
        v.institutionFreeText !== undefined &&
        v.institutionFreeText !== null &&
        v.institutionFreeText.length > 0;
      return hasInst !== hasFreeText;
    },
    {
      message:
        'Pontosan az egyik kötelező: institutionId (claimed/verified út) VAGY institutionFreeText (free_text út).',
      path: ['institutionId'],
    },
  )
  .refine(
    (v) => {
      if (!v.institutionBindingStatus) return true;
      const hasInst = v.institutionId !== undefined && v.institutionId !== null;
      const hasFreeText =
        v.institutionFreeText !== undefined && v.institutionFreeText !== null;
      if (v.institutionBindingStatus === 'free_text') return hasFreeText && !hasInst;
      return hasInst && !hasFreeText;
    },
    {
      message: 'institutionBindingStatus inkonzisztens a payload-dal.',
      path: ['institutionBindingStatus'],
    },
  );

export const GroupUpdateSchema = z.object({
  schoolYearId: z.number().int().positive().optional(),
  groupTypeId: z.number().int().positive().optional(),
  name: z.string().min(1).max(100).optional(),
  notes: z.string().nullable().optional(),
  institutionFreeText: z.string().min(1).max(500).nullable().optional(),
});

// -------- Students --------

/**
 * Ország-specifikus oktatási azonosító Zod factory.
 * A countryCode szerint hív validateEducationalId-t. Default HU.
 */
export function makeEducationalIdSchema(countryCode = 'HU') {
  return z
    .string()
    .min(1)
    .superRefine((value, ctx) => {
      const result = validateEducationalId(value, countryCode);
      if (result.ok) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result.errorKey,
      });
    });
}

/**
 * Default oktatási azonosító Zod sémája (HU).
 */
export const EducationalIdSchema = makeEducationalIdSchema('HU');

export const StudentCreateSchema = z
  .object({
    institutionId: z.number().int().positive().nullable(),
    educationalId: EducationalIdSchema.nullable().optional(),
    identityStatus: IdentityStatusSchema.optional(),
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    birthDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    addressLine: z.string().max(500).nullable().optional(),
    city: z.string().max(200).nullable().optional(),
    postalCode: z.string().max(20).nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .refine(
    (v) => {
      if (v.identityStatus === 'identified' || v.identityStatus === 'verified') {
        return !!v.educationalId;
      }
      return true;
    },
    {
      message: "identityStatus 'identified'/'verified' esetén educationalId kötelező.",
      path: ['educationalId'],
    },
  );

export const StudentUpdateSchema = z
  .object({
    educationalId: EducationalIdSchema.nullable().optional(),
    identityStatus: IdentityStatusSchema.optional(),
    firstName: z.string().min(1).max(100).optional(),
    lastName: z.string().min(1).max(100).optional(),
    birthDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    addressLine: z.string().max(500).nullable().optional(),
    city: z.string().max(200).nullable().optional(),
    postalCode: z.string().max(20).nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .refine(
    (v) => {
      if (v.identityStatus === 'identified' || v.identityStatus === 'verified') {
        return true;
      }
      return true;
    },
    { message: '', path: [] },
  );

// -------- Lifecycle (F1f) --------

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'ISO dátum formátum: YYYY-MM-DD');

export const AdvanceGroupSchema = z
  .object({
    mode: z.enum(['create', 'assign']),
    targetSchoolYearId: z.number().int().positive().optional(),
    targetName: z.string().min(1).max(100).optional(),
    targetGroupTypeId: z.number().int().positive().optional(),
    targetGroupId: z.number().int().positive().optional(),
    effectiveDate: IsoDate.optional(),
    deactivateOldGroup: z.boolean().optional(),
    reason: z.string().min(1).max(50).optional(),
  })
  .refine(
    (v) => (v.mode === 'create' ? v.targetSchoolYearId !== undefined : true),
    { message: 'mode=create esetén a targetSchoolYearId kötelező', path: ['targetSchoolYearId'] },
  )
  .refine(
    (v) => (v.mode === 'assign' ? v.targetGroupId !== undefined : true),
    { message: 'mode=assign esetén a targetGroupId kötelező', path: ['targetGroupId'] },
  );

export const TransferStudentSchema = z.object({
  targetGroupId: z.number().int().positive(),
  effectiveDate: IsoDate.optional(),
  reason: z.string().min(1).max(50).optional(),
});

export const LeaveStudentSchema = z.object({
  effectiveDate: IsoDate.optional(),
  deactivateStudent: z.boolean().optional(),
  reason: z.string().min(1).max(50).optional(),
});

// -------- Error reporting (F1g) --------

export const ErrorSourceSchema = z.enum(['BE', 'FE', 'FEA', 'FEB']);
export const ErrorSeveritySchema = z.enum(['fatal', 'error', 'warn', 'info']);

export const ErrorReportInputSchema = z.object({
  source: z.enum(['FE', 'FEA', 'FEB']),
  severity: ErrorSeveritySchema.optional(),
  message: z.string().min(1).max(2000),
  stack: z.string().max(20000).optional(),
  context: z.record(z.string(), z.unknown()).optional(),
  url: z.string().max(2000).optional(),
  clientTs: z.string().datetime().optional(),
});

// -------- i18n (F1h) --------

export const I18nLanguageSchema = z
  .string()
  .regex(/^[a-z]{2}(-[A-Z]{2})?$/, 'ISO 639-1 kód, esetleg ország-postfix: hu, en-US');

export const I18nNamespaceSchema = z.enum(['common', 'refdata', 'fe', 'fea', 'feb']);

export const I18nKeySchema = z.string().min(1).max(200);

export const TranslationUpsertSchema = z.object({
  value: z.string().min(1).max(5000),
});

// -------- Admin logs (F1i) --------

const IsoTimestamp = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/,
    'ISO 8601 időbélyeg',
  );

export const ErrorLogsQuerySchema = z.object({
  source: ErrorSourceSchema.optional(),
  severity: ErrorSeveritySchema.optional(),
  fingerprint: z.string().min(1).max(128).optional(),
  userId: z.coerce.number().int().positive().optional(),
  textSearch: z.string().min(1).max(200).optional(),
  from: IsoTimestamp.optional(),
  to: IsoTimestamp.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const AuditEventsQuerySchema = z.object({
  userId: z.coerce.number().int().positive().optional(),
  action: z.string().min(1).max(100).optional(),
  entityType: z.string().min(1).max(100).optional(),
  entityId: z.string().min(1).max(100).optional(),
  scopeType: ScopeTypeSchema.optional(),
  scopeId: z.string().min(1).max(100).optional(),
  textSearch: z.string().min(1).max(200).optional(),
  from: IsoTimestamp.optional(),
  to: IsoTimestamp.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const LogBulkDeleteSchema = z.object({
  before: IsoTimestamp,
  source: ErrorSourceSchema.optional(),
  severity: ErrorSeveritySchema.optional(),
  fingerprint: z.string().min(1).max(128).optional(),
});

// -------- Finance: legal profiles + tax profiles (F2a) --------

export const PaymentMethodSchema = z.enum(['cash', 'bank_transfer', 'card', 'escrow']);

export const LegalEntityTypeSchema = z.enum(['foundation', 'association', 'other']);

const ProfileCodeSchema = z
  .string()
  .min(1)
  .max(50)
  .regex(/^[a-z0-9_]+$/, 'Csak kisbetű, szám és aláhúzás (pl. hu_foundation).');

const I18nKeyShort = z.string().min(1).max(200);

export const TaxProfileCreateSchema = z.object({
  countryCode: CountryCodeSchema,
  code: ProfileCodeSchema,
  nameKey: I18nKeyShort,
  descriptionKey: I18nKeyShort,
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});

export const TaxProfileUpdateSchema = TaxProfileCreateSchema.partial().omit({
  countryCode: true,
  code: true,
});

export const LegalProfileCreateSchema = z
  .object({
    countryCode: CountryCodeSchema,
    code: ProfileCodeSchema,
    nameKey: I18nKeyShort,
    descriptionKey: I18nKeyShort,
    allowedPaymentMethods: z.array(PaymentMethodSchema).min(1),
    requiresReceiptForExpense: z.boolean().optional(),
    requiresInvoiceForExpense: z.boolean().optional(),
    requiresDualApproval: z.boolean().optional(),
    dualApprovalThresholdMinorUnit: z.number().int().nonnegative().nullable().optional(),
    requiresAnnualReport: z.boolean().optional(),
    annualReportDocumentKeys: z.array(I18nKeyShort).optional(),
    hasLegalEntity: z.boolean().optional(),
    legalEntityType: LegalEntityTypeSchema.nullable().optional(),
    taxProfileId: z.number().int().positive().nullable().optional(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.requiresDualApproval === true ||
      v.dualApprovalThresholdMinorUnit === undefined ||
      v.dualApprovalThresholdMinorUnit === null,
    {
      message:
        'requiresDualApproval=false esetén a dualApprovalThresholdMinorUnit null kell legyen.',
      path: ['dualApprovalThresholdMinorUnit'],
    },
  );

export const LegalProfileUpdateSchema = z
  .object({
    nameKey: I18nKeyShort.optional(),
    descriptionKey: I18nKeyShort.optional(),
    allowedPaymentMethods: z.array(PaymentMethodSchema).min(1).optional(),
    requiresReceiptForExpense: z.boolean().optional(),
    requiresInvoiceForExpense: z.boolean().optional(),
    requiresDualApproval: z.boolean().optional(),
    dualApprovalThresholdMinorUnit: z.number().int().nonnegative().nullable().optional(),
    requiresAnnualReport: z.boolean().optional(),
    annualReportDocumentKeys: z.array(I18nKeyShort).optional(),
    hasLegalEntity: z.boolean().optional(),
    legalEntityType: LegalEntityTypeSchema.nullable().optional(),
    taxProfileId: z.number().int().positive().nullable().optional(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.requiresDualApproval !== false ||
      v.dualApprovalThresholdMinorUnit === undefined ||
      v.dualApprovalThresholdMinorUnit === null,
    {
      message:
        'requiresDualApproval=false esetén a dualApprovalThresholdMinorUnit null kell legyen.',
      path: ['dualApprovalThresholdMinorUnit'],
    },
  );

export const AssignLegalProfileSchema = z.object({
  legalProfileId: z.number().int().positive(),
  validFrom: IsoDate,
  notes: z.string().max(2000).nullable().optional(),
});

// -------- Finance: currency + system_settings + exchange rates + payment requirements (F2b) --------

const MoneyAmountString = z
  .string()
  .regex(/^\d{1,12}(\.\d{1,2})?$/, 'Összeg formátum: max 12 egész jegy, opcionálisan 1-2 tizedes.');

const ExchangeRateString = z
  .string()
  .regex(/^\d{1,10}(\.\d{1,8})?$/, 'Árfolyam formátum: max 10 egész jegy, max 8 tizedes.')
  .refine((v) => Number(v) > 0, { message: 'Árfolyam > 0 kell legyen.' });

export const CurrencyCreateSchema = z.object({
  code: CurrencyCodeSchema,
  nameKey: I18nKeyShort,
  minorUnit: z.number().int().min(0).max(4).optional(),
  symbol: z.string().max(10).nullable().optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});

export const CurrencyUpdateSchema = z.object({
  nameKey: I18nKeyShort.optional(),
  minorUnit: z.number().int().min(0).max(4).optional(),
  symbol: z.string().max(10).nullable().optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});

export const SystemSettingScopeTypeSchema = z.enum([
  'global',
  'country',
  'institution',
  'group',
]);

export const SystemSettingUpsertSchema = z
  .object({
    scopeType: SystemSettingScopeTypeSchema,
    scopeId: z.string().max(100).nullable(),
    key: z.string().min(1).max(200),
    value: z.unknown(),
  })
  .refine(
    (v) => (v.scopeType === 'global' ? v.scopeId === null : v.scopeId !== null && v.scopeId.length > 0),
    {
      message: 'global scope esetén scopeId null, egyébként nem-üres string kell.',
      path: ['scopeId'],
    },
  );

export const SystemSettingsQuerySchema = z.object({
  scopeType: SystemSettingScopeTypeSchema.optional(),
  scopeId: z.string().max(100).optional(),
  key: z.string().max(200).optional(),
});

export const ExchangeRateSourceSchema = z.enum(['mnb', 'ecb', 'manual']);

export const ExchangeRateManualInputSchema = z
  .object({
    baseCurrency: CurrencyCodeSchema,
    quoteCurrency: CurrencyCodeSchema,
    rate: ExchangeRateString,
    validFrom: IsoDate,
  })
  .refine((v) => v.baseCurrency !== v.quoteCurrency, {
    message: 'baseCurrency és quoteCurrency nem lehet azonos.',
    path: ['quoteCurrency'],
  });

export const ExchangeRateQuerySchema = z.object({
  baseCurrency: CurrencyCodeSchema.optional(),
  quoteCurrency: CurrencyCodeSchema.optional(),
  source: ExchangeRateSourceSchema.optional(),
  atDate: IsoDate.optional(),
  openOnly: z.coerce.boolean().optional(),
});

export const PaymentRequirementStatusSchema = z.enum([
  'draft',
  'published',
  'cancelled',
  'closed',
]);

const PaymentRequirementTargetInputSchema = z
  .object({
    studentId: z.number().int().positive(),
    expectedAmount: MoneyAmountString.optional(),
    waived: z.boolean().optional(),
    waiveReason: z.string().max(500).nullable().optional(),
  })
  .refine(
    (v) => !v.waived || (v.waiveReason !== null && v.waiveReason !== undefined && v.waiveReason.length > 0),
    { message: 'waived=true esetén waiveReason kötelező.', path: ['waiveReason'] },
  );

export const PaymentRequirementCreateSchema = z.object({
  groupId: z.number().int().positive(),
  title: z.string().min(1).max(200),
  description: z.string().max(5000).nullable().optional(),
  purpose: z.string().min(1).max(200),
  amount: MoneyAmountString,
  currency: CurrencyCodeSchema,
  dueDate: IsoDate.nullable().optional(),
  targets: z.array(PaymentRequirementTargetInputSchema).nullable().optional(),
});

export const PaymentRequirementUpdateSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(5000).nullable().optional(),
  purpose: z.string().min(1).max(200).optional(),
  amount: MoneyAmountString.optional(),
  currency: CurrencyCodeSchema.optional(),
  dueDate: IsoDate.nullable().optional(),
  updateMode: z.enum(['amount_only', 'full']).optional(),
});

export const PaymentRequirementPublishSchema = z.object({
  publishedAt: z
    .string()
    .regex(
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/,
      'ISO 8601 időbélyeg',
    )
    .optional(),
});

export const PaymentRequirementCancelSchema = z.object({
  cancelReason: z.string().min(1).max(500),
});

export const PaymentRequirementTargetUpsertSchema = z
  .object({
    studentId: z.number().int().positive(),
    expectedAmount: MoneyAmountString,
    waived: z.boolean().optional(),
    waiveReason: z.string().max(500).nullable().optional(),
  })
  .refine(
    (v) => !v.waived || (v.waiveReason !== null && v.waiveReason !== undefined && v.waiveReason.length > 0),
    { message: 'waived=true esetén waiveReason kötelező.', path: ['waiveReason'] },
  );

export const PaymentRequirementTargetUpdateSchema = z
  .object({
    expectedAmount: MoneyAmountString.optional(),
    waived: z.boolean().optional(),
    waiveReason: z.string().max(500).nullable().optional(),
  })
  .refine(
    (v) => v.waived !== true || (v.waiveReason !== null && v.waiveReason !== undefined && v.waiveReason.length > 0),
    { message: 'waived=true esetén waiveReason kötelező.', path: ['waiveReason'] },
  );

export const PaymentRequirementsListQuerySchema = z.object({
  status: PaymentRequirementStatusSchema.optional(),
  includeTargets: z.coerce.boolean().optional(),
});

// -------- Cashier grants --------

const IsoTimestampOptional = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/,
    'ISO 8601 időbélyeg',
  );

export const CashierGrantInputSchema = z
  .object({
    userId: z.number().int().positive(),
    validFrom: IsoTimestampOptional.nullable().optional(),
    validTo: IsoTimestampOptional.nullable().optional(),
  })
  .refine(
    (v) => {
      if (!v.validFrom || !v.validTo) return true;
      return new Date(v.validTo).getTime() > new Date(v.validFrom).getTime();
    },
    { message: 'validTo > validFrom kell legyen.', path: ['validTo'] },
  );

export const CashierGrantUpdateInputSchema = z.object({
  validTo: IsoTimestampOptional.nullable(),
});

export const UserSearchQuerySchema = z.object({
  q: z.string().min(1).max(100),
  groupId: z.coerce.number().int().positive(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

// -------- Cash balance --------

export const CashBalanceUpsertSchema = z.object({
  cashAmount: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/, 'NUMERIC(14,2) formátum'),
  bankAmount: z.string().regex(/^\d{1,12}(\.\d{1,2})?$/, 'NUMERIC(14,2) formátum'),
  asOfDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'ISO dátum YYYY-MM-DD'),
  notes: z.string().max(2000).nullable().optional(),
});

// -------- F2e/F2f: payments + expenses --------

export const PaymentCreateSchema = z.object({
  targetId: z.number().int().positive(),
  amount: MoneyAmountString,
  paidAt: IsoDate,
  paymentMethod: PaymentMethodSchema,
  externalRef: z.string().max(200).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const PaymentUpdateSchema = z.object({
  amount: MoneyAmountString.optional(),
  paidAt: IsoDate.optional(),
  paymentMethod: PaymentMethodSchema.optional(),
  externalRef: z.string().max(200).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const ExpenseCreateSchema = z.object({
  requirementId: z.number().int().positive().nullable().optional(),
  purpose: z.string().min(1).max(200),
  amount: MoneyAmountString,
  spentAt: IsoDate,
  paymentMethod: PaymentMethodSchema,
  payeeName: z.string().max(200).nullable().optional(),
  invoiceNumber: z.string().max(100).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const ExpenseUpdateSchema = z.object({
  requirementId: z.number().int().positive().nullable().optional(),
  purpose: z.string().min(1).max(200).optional(),
  amount: MoneyAmountString.optional(),
  spentAt: IsoDate.optional(),
  paymentMethod: PaymentMethodSchema.optional(),
  payeeName: z.string().max(200).nullable().optional(),
  invoiceNumber: z.string().max(100).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const PaymentsListQuerySchema = z.object({
  targetId: z.coerce.number().int().positive().optional(),
  requirementId: z.coerce.number().int().positive().optional(),
  fromDate: IsoDate.optional(),
  toDate: IsoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export const ExpensesListQuerySchema = z.object({
  requirementId: z.coerce.number().int().positive().optional(),
  fromDate: IsoDate.optional(),
  toDate: IsoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

// -------- Reports --------

export const ReportViewSchema = z.enum(['by-student', 'by-requirement', 'by-method']);
export const ReportRowStatusSchema = z.enum(['paid', 'partial', 'unpaid', 'waived']);

export const ReportsQuerySchema = z.object({
  paidFrom: IsoDate.optional(),
  paidTo: IsoDate.optional(),
  dueFrom: IsoDate.optional(),
  dueTo: IsoDate.optional(),
  paymentMethod: PaymentMethodSchema.optional(),
  status: ReportRowStatusSchema.optional(),
});
